import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, formatApiError, downloadCrmExcel, downloadCrmPdf } from "../../lib/api";
import { Button } from "../ui/button";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, LineChart, Line, Legend,
} from "recharts";
import { TrendingUp, FileSpreadsheet, FileText, Percent, CheckCircle2, XCircle, HeartHandshake, Star } from "lucide-react";

const PIE_COLORS = ["#ef4444", "#f97316", "#f59e0b", "#8b5cf6", "#ec4899", "#64748b"];

function Kpi({ icon: Icon, label, value, grad }) {
  return (
    <div className="rounded-2xl p-4 border border-white/10 bg-white/[0.03]">
      <div className="flex items-center justify-between">
        <span className="h-10 w-10 rounded-xl flex items-center justify-center text-white" style={{ background: grad }}><Icon className="h-5 w-5" /></span>
        <span className="text-2xl font-display font-bold">{value}</span>
      </div>
      <p className="text-xs text-muted-foreground mt-2">{label}</p>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="rounded-2xl border border-white/10 p-5 bg-white/[0.02]">
      <h3 className="font-display text-base font-semibold mb-4">{title}</h3>
      {children}
    </div>
  );
}

export default function StatsTab() {
  const { t } = useTranslation();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    (async () => {
      try { const r = await api.get("/crm/stats"); setS(r.data); }
      catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    })();
  }, []);

  const doExport = async (kind) => {
    setBusy(kind);
    try { kind === "excel" ? await downloadCrmExcel() : await downloadCrmPdf(); toast.success(t("crm.stats.exportDone")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  if (!s) return <p className="text-muted-foreground text-sm py-8">{t("crm.stats.loading")}</p>;

  const maxFunnel = Math.max(1, ...s.funnel.map((f) => f.count));
  const stageLabel = (f) => t(`crm.stats.funnelStages.${f.key}`, { defaultValue: f.stage });

  return (
    <div className="space-y-4" data-testid="crm-stats-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-display text-lg font-semibold flex items-center gap-2"><TrendingUp className="h-4 w-4 text-emerald-400" /> {t("crm.stats.title")}</h3>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => doExport("excel")} disabled={busy} data-testid="crm-export-excel"><FileSpreadsheet className="h-4 w-4 mr-2" /> {t("crm.stats.excel")}</Button>
          <Button variant="outline" onClick={() => doExport("pdf")} disabled={busy} data-testid="crm-export-pdf"><FileText className="h-4 w-4 mr-2" /> {t("crm.stats.pdf")}</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi icon={Percent} label={t("crm.stats.conversion")} value={`${s.conversion}%`} grad="linear-gradient(135deg,#059669,#10b981)" />
        <Kpi icon={CheckCircle2} label={t("crm.stats.admissions")} value={s.admitted} grad="linear-gradient(135deg,#22c55e,#16a34a)" />
        <Kpi icon={XCircle} label={t("crm.stats.refused")} value={s.refused} grad="linear-gradient(135deg,#ef4444,#fb7185)" />
        <Kpi icon={TrendingUp} label={t("crm.stats.prospects")} value={s.total_prospects} grad="linear-gradient(135deg,#8b5cf6,#a855f7)" />
      </div>

      {s.events_roi && (
        <Card title={t("crm.stats.eventsRoi")}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="crm-events-roi">
            <div className="rounded-xl border border-white/10 p-3 bg-white/[0.02]"><div className="text-[11px] text-muted-foreground">{t("crm.stats.roiEvents")}</div><div className="text-xl font-bold text-pink-400">{s.events_roi.events}</div></div>
            <div className="rounded-xl border border-white/10 p-3 bg-white/[0.02]"><div className="text-[11px] text-muted-foreground">{t("crm.stats.roiParticipants")}</div><div className="text-xl font-bold text-sky-400">{s.events_roi.participants}</div></div>
            <div className="rounded-xl border border-white/10 p-3 bg-white/[0.02]"><div className="text-[11px] text-muted-foreground">{t("crm.stats.roiProspects")}</div><div className="text-xl font-bold text-violet-400">{s.events_roi.new_prospects}</div></div>
            <div className="rounded-xl border border-white/10 p-3 bg-white/[0.02]"><div className="text-[11px] text-muted-foreground">{t("crm.stats.roiAdmissions")}</div><div className="text-xl font-bold text-emerald-400">{s.events_roi.admissions}</div></div>
            <div className="rounded-xl border border-white/10 p-3 bg-white/[0.02]"><div className="text-[11px] text-muted-foreground">{t("crm.stats.roiCost")}</div><div className="text-xl font-bold">{s.events_roi.cost} €</div></div>
            <div className="rounded-xl border border-white/10 p-3 bg-white/[0.02]"><div className="text-[11px] text-muted-foreground">{t("crm.stats.roiCostProspect")}</div><div className="text-xl font-bold text-amber-400">{s.events_roi.cost_per_prospect} €</div></div>
            <div className="rounded-xl border border-white/10 p-3 bg-white/[0.02]"><div className="text-[11px] text-muted-foreground">{t("crm.stats.roiCostAdmission")}</div><div className="text-xl font-bold text-amber-400">{s.events_roi.cost_per_admission} €</div></div>
          </div>
        </Card>
      )}

      <Card title={t("crm.stats.funnel")}>
        <div className="space-y-2" data-testid="crm-funnel">
          {s.funnel.map((f, i) => (
            <div key={f.key} className="flex items-center gap-3">
              <div className="w-52 shrink-0 text-sm text-foreground/80">{stageLabel(f)}</div>
              <div className="flex-1 h-7 rounded-lg bg-white/5 overflow-hidden">
                <div className="h-full rounded-lg flex items-center justify-end pr-2 text-xs font-semibold text-white transition-all"
                  style={{ width: `${(f.count / maxFunnel) * 100}%`, background: `hsl(${160 - i * 12},70%,45%)` }}>{f.count}</div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title={t("crm.stats.monthly")}>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={s.monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
              <XAxis dataKey="month" tick={{ fill: "#94a3b8", fontSize: 11 }} />
              <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} />
              <Legend />
              <Line type="monotone" dataKey="new" name={t("crm.stats.newLine")} stroke="#8b5cf6" strokeWidth={2} />
              <Line type="monotone" dataKey="admitted" name={t("crm.stats.admittedLine")} stroke="#10b981" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        <Card title={t("crm.stats.refusalReasons")}>
          {s.refusals.length === 0 ? <p className="text-sm text-muted-foreground py-8 text-center">{t("crm.stats.noRefusal")}</p> : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={s.refusals} dataKey="count" nameKey="reason" cx="50%" cy="50%" outerRadius={80}>
                  {s.refusals.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      <Card title={t("crm.stats.bySource")}>
        {s.by_source.length === 0 ? <p className="text-sm text-muted-foreground">{t("crm.stats.noData")}</p> : (
          <ResponsiveContainer width="100%" height={Math.max(160, s.by_source.length * 42)}>
            <BarChart data={s.by_source} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
              <XAxis type="number" tick={{ fill: "#94a3b8", fontSize: 11 }} allowDecimals={false} />
              <YAxis type="category" dataKey="source" width={130} tick={{ fill: "#cbd5e1", fontSize: 11 }} />
              <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} />
              <Legend />
              <Bar dataKey="total" name={t("crm.stats.contacts")} fill="#6366f1" radius={[0, 4, 4, 0]} />
              <Bar dataKey="admitted" name={t("crm.stats.admissions")} fill="#10b981" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title={t("crm.stats.byPartner")}>
          {s.by_partner.length === 0 ? <p className="text-sm text-muted-foreground">{t("crm.stats.noPartner")}</p> : (
            <div className="space-y-2">
              {s.by_partner.map((p) => (
                <div key={p.name} className="flex items-center justify-between text-sm border-b border-white/5 pb-1.5">
                  <span className="flex items-center gap-2"><HeartHandshake className="h-4 w-4 text-emerald-400" /> {p.name}</span>
                  <span className="text-muted-foreground">{p.referrals} {t("crm.stats.referralsUnit")} · {p.admitted} {t("crm.stats.admittedUnit")} · {p.rate}%</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card title={t("crm.stats.postAdmission")}>
          {s.post_admission.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">{t("crm.stats.noEntered")}</p> : (
            <div className="space-y-2">
              {s.post_admission.map((r, i) => (
                <div key={i} className="flex items-center justify-between text-sm border-b border-white/5 pb-1.5">
                  <span>{r.name}<span className="text-xs text-muted-foreground ml-2">{r.entry_date}</span></span>
                  <span className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`h-3.5 w-3.5 ${n <= (r.satisfaction || 0) ? "text-amber-400 fill-amber-400" : "text-white/15"}`} />)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
